/**
 * ZCollections - Zod Validation Schemas for Collections API
 *
 * Single source of truth for Collections domain API validation.
 * These schemas generate both runtime validation (Zod) and compile-time types (TApi).
 *
 * Naming: Z prefix = Zod schemas (vs TApi = TypeScript types, DbEntity = Prisma)
 */

import { z } from "zod";

import type { DbEntity } from "../../../client";
import { ApiSuccessSchema } from "../../common/response.schema";

// ═══════════════════════════════════════════════════════════════
// SHARED SCHEMAS - Reusable pieces
// ═══════════════════════════════════════════════════════════════

/**
 * Collection ID validation (UUID)
 */
const CollectionIdSchema = z.string().uuid("Invalid collection ID format");

/**
 * Document ID validation (UUID)
 */
const DocumentIdSchema = z.string().uuid("Invalid document ID format");

/**
 * Collection name validation.
 *
 * `.trim()` precedes `.min(1)` and that order is the whole guard: Zod runs the
 * chain left to right, so `.min(1).trim()` measures the untrimmed value and
 * admits `"   "`, which `Collection.name` (NOT NULL, `@unique`) then stores as
 * a machine identifier nothing can match and no screen can render.
 */
const CollectionNameSchema = z
  .string()
  .trim()
  .min(1, "Collection name is required")
  .max(255, "Collection name must be 255 characters or less");

/**
 * `Collection.displayName` is what every collection surface labels the row
 * with, falling back to `name` only when it is absent. A blank one is NOT
 * absent — it wins the fallback and renders nothing — so the write contract
 * refuses it.
 *
 * This removes no working way to clear the field: the column is nullable but
 * neither body schema is `.nullable()`, so `""` was the only spelling a caller
 * had, and it set a blank label rather than restoring the `name` fallback.
 * A real clear needs `.nullable()` on both bodies and a use case that maps
 * `null` to `null` — a contract change, not a validation one.
 */
const CollectionDisplayNameSchema = z
  .string()
  .trim()
  .min(1, "Collection display name must not be blank")
  .max(255, "Collection display name must be 255 characters or less");

/**
 * Search query validation
 */
const SearchQuerySchema = z
  .string()
  .trim()
  .min(1, "Search query is required")
  .max(1000, "Search query must be 1000 characters or less");

/**
 * K parameter (number of results)
 */
const KParameterSchema = z
  .number()
  .int("K must be an integer")
  .min(1, "K must be at least 1")
  .max(100, "K must be 100 or less")
  .optional();

// ═══════════════════════════════════════════════════════════════
// REQUEST SCHEMAS - Input validation
// ═══════════════════════════════════════════════════════════════

/**
 * LIST - Query parameters for listing collections
 */
export const CollectionListParamsSchema = z.object({
  search: z.string().optional(),
  isActive: z
    .union([z.boolean(), z.string()])
    .transform((val) => {
      if (typeof val === "boolean") return val;
      return val === "true";
    })
    .optional(),
  limit: z
    .union([z.number(), z.string()])
    .transform((val) => {
      const num = typeof val === "string" ? parseInt(val, 10) : val;
      return isNaN(num) ? 20 : num;
    })
    .pipe(z.number().int().min(1).max(100))
    .optional(),
  offset: z
    .union([z.number(), z.string()])
    .transform((val) => {
      const num = typeof val === "string" ? parseInt(val, 10) : val;
      return isNaN(num) ? 0 : num;
    })
    .pipe(z.number().int().min(0))
    .optional()
});

/**
 * GET - Path variables for getting a single collection
 */
export const CollectionGetPathVarsSchema = z.object({
  id: CollectionIdSchema
});

/**
 * CREATE - Request body for creating a collection
 */
export const CollectionCreateBodySchema = z.object({
  name: CollectionNameSchema,
  displayName: CollectionDisplayNameSchema.optional(),
  description: z.string().optional(),
  k: KParameterSchema.default(10),
  reranker: z.string().optional(),
  preciseResponses: z.boolean().optional().default(false),
  includeMetadata: z.boolean().optional().default(false),
  metadata: z.record(z.string(), z.unknown()).optional(),
  documentIds: z.array(DocumentIdSchema).optional()
});

/**
 * UPDATE - Request body for updating a collection
 */
export const CollectionUpdateBodySchema = z.object({
  displayName: CollectionDisplayNameSchema.optional(),
  description: z.string().optional(),
  k: KParameterSchema,
  reranker: z.string().optional(),
  preciseResponses: z.boolean().optional(),
  includeMetadata: z.boolean().optional(),
  isActive: z.boolean().optional(),
  metadata: z.record(z.string(), z.unknown()).optional()
});

/**
 * UPDATE - Path variables
 */
export const CollectionUpdatePathVarsSchema = z.object({
  id: CollectionIdSchema
});

/**
 * DELETE - Path variables
 */
export const CollectionDeletePathVarsSchema = z.object({
  id: CollectionIdSchema
});

/**
 * SEARCH - Path variables
 */
export const CollectionSearchPathVarsSchema = z.object({
  id: CollectionIdSchema
});

/**
 * SEARCH - Request body for searching within a collection
 */
export const CollectionSearchBodySchema = z.object({
  query: SearchQuerySchema,
  k: KParameterSchema
});

/**
 * TEST QUERY - Path variables
 */
export const CollectionTestQueryPathVarsSchema = z.object({
  id: CollectionIdSchema
});

/**
 * TEST QUERY - Request body
 */
export const CollectionTestQueryBodySchema = z.object({
  query: SearchQuerySchema,
  preciseResponses: z.boolean().optional()
});

/**
 * GET DOCUMENTS - Path variables
 */
export const CollectionGetDocumentsPathVarsSchema = z.object({
  id: CollectionIdSchema
});

/**
 * ADD DOCUMENTS - Path variables
 */
export const CollectionAddDocumentsPathVarsSchema = z.object({
  id: CollectionIdSchema
});

/**
 * ADD DOCUMENTS - Request body
 */
export const CollectionAddDocumentsBodySchema = z.object({
  documentIds: z
    .array(DocumentIdSchema)
    .min(1, "At least one document ID is required")
    .max(100, "Cannot add more than 100 documents at once")
});

/**
 * REMOVE DOCUMENT - Path variables
 */
export const CollectionRemoveDocumentPathVarsSchema = z.object({
  id: CollectionIdSchema,
  documentId: DocumentIdSchema
});

/**
 * TASK/AGENT ATTACHMENT - Path variables
 */
export const TaskCollectionsPathVarsSchema = z.object({
  taskId: z.string().uuid("Invalid task ID format")
});

export const AgentCollectionsPathVarsSchema = z.object({
  agentId: z.string().uuid("Invalid agent ID format")
});

/**
 * ATTACH/DETACH - Request body (collection IDs)
 */
export const CollectionIdsBodySchema = z.object({
  collectionIds: z
    .array(CollectionIdSchema)
    .min(1, "At least one collection ID is required")
    .max(50, "Cannot attach more than 50 collections at once")
});

// ═══════════════════════════════════════════════════════════════
// RESPONSE SCHEMAS - Output validation
// ═══════════════════════════════════════════════════════════════

/**
 * Collection response - validates critical fields only
 * (Prisma guarantees the full structure)
 */
export const CollectionResponseSchema = z.custom<DbEntity.Collection>(
  (data): data is DbEntity.Collection => {
    return (
      typeof data === "object" &&
      data !== null &&
      "id" in data &&
      typeof data.id === "string" &&
      "name" in data &&
      typeof data.name === "string" &&
      "organizationId" in data &&
      typeof data.organizationId === "string"
    );
  },
  {
    message: "Invalid collection response: missing required fields (id, name, organizationId)"
  }
);

/**
 * LIST - Response with collections array and total count
 */
export const CollectionListResponseSchema = z.object({
  collections: z.array(CollectionResponseSchema),
  total: z.number().int().min(0)
});

/**
 * SEARCH - Response with search results
 */
export const CollectionSearchResponseSchema = z.object({
  results: z.array(z.record(z.string(), z.unknown()).and(z.object({ id: z.string().optional() }))),
  total: z.number().int().min(0),
  query: z.string(),
  collections: z.array(z.string())
});

/**
 * TEST QUERY - Response with query results and sources
 */
export const CollectionTestQueryResponseSchema = z.object({
  query: z.string(),
  sources: z.array(
    z.object({
      documentName: z.string(),
      score: z.number(),
      content: z.string()
    })
  ),
  collectionId: z.string(),
  preciseResponses: z.boolean(),
  answer: z.string().optional()
});

/**
 * GET DOCUMENTS - Response with documents array
 */
export const CollectionGetDocumentsResponseSchema = z.array(
  z.custom<DbEntity.Document>((data): data is DbEntity.Document => {
    return typeof data === "object" && data !== null && "id" in data && typeof data.id === "string";
  })
);

/**
 * ADD DOCUMENTS - Response (success indicator)
 */
export const CollectionAddDocumentsResponseSchema = z.record(z.string(), z.unknown());

/**
 * REMOVE DOCUMENT - Response (success indicator)
 */
export const CollectionRemoveDocumentResponseSchema = z.record(z.string(), z.unknown());

/**
 * Collections attached to task/agent
 */
export const CollectionsAttachmentResponseSchema = z.object({
  collections: z.array(CollectionResponseSchema)
});

/**
 * Attach/Detach response (success indicator)
 */
export const AttachmentActionResponseSchema = z.record(z.string(), z.unknown());

// ═══════════════════════════════════════════════════════════════
// ZCOLLECTIONS MANIFEST - Export all schemas for runtime use
// ═══════════════════════════════════════════════════════════════

/**
 * ZCollections - Zod API validation schemas
 *
 * Single source of truth for Collections API:
 * - Runtime validation (backend + frontend)
 * - Type generation (TApi auto-inferred via InferSchemas)
 *
 * Z prefix = Zod schemas (vs DbEntity = Prisma, TApi = TypeScript)
 */
export const ZCollections = {
  List: {
    method: "GET" as const,
    path: "/api/collections",
    Params: CollectionListParamsSchema,
    Response: ApiSuccessSchema(CollectionListResponseSchema)
  },
  Get: {
    method: "GET" as const,
    path: "/api/collections/:id",
    PathVars: CollectionGetPathVarsSchema,
    Response: ApiSuccessSchema(CollectionResponseSchema)
  },
  Create: {
    method: "POST" as const,
    path: "/api/collections",
    Body: CollectionCreateBodySchema,
    Response: ApiSuccessSchema(CollectionResponseSchema)
  },
  Update: {
    method: "PUT" as const,
    path: "/api/collections/:id",
    PathVars: CollectionUpdatePathVarsSchema,
    Body: CollectionUpdateBodySchema,
    Response: ApiSuccessSchema(CollectionResponseSchema)
  },
  Delete: {
    method: "DELETE" as const,
    path: "/api/collections/:id",
    PathVars: CollectionDeletePathVarsSchema,
    Response: ApiSuccessSchema(z.void())
  },
  Search: {
    method: "POST" as const,
    path: "/api/collections/:id/search/v2",
    PathVars: CollectionSearchPathVarsSchema,
    Body: CollectionSearchBodySchema,
    Response: ApiSuccessSchema(CollectionSearchResponseSchema)
  },
  TestQuery: {
    method: "POST" as const,
    path: "/api/collections/:id/test-query",
    PathVars: CollectionTestQueryPathVarsSchema,
    Body: CollectionTestQueryBodySchema,
    Response: ApiSuccessSchema(CollectionTestQueryResponseSchema)
  },
  GetDocuments: {
    method: "GET" as const,
    path: "/api/collections/:id/documents",
    PathVars: CollectionGetDocumentsPathVarsSchema,
    Response: ApiSuccessSchema(CollectionGetDocumentsResponseSchema)
  },
  AddDocuments: {
    method: "POST" as const,
    path: "/api/collections/:id/documents",
    PathVars: CollectionAddDocumentsPathVarsSchema,
    Body: CollectionAddDocumentsBodySchema,
    Response: ApiSuccessSchema(CollectionAddDocumentsResponseSchema)
  },
  RemoveDocument: {
    method: "DELETE" as const,
    path: "/api/collections/:id/documents/:documentId",
    PathVars: CollectionRemoveDocumentPathVarsSchema,
    Response: ApiSuccessSchema(z.void())
  },
  GetTaskCollections: {
    method: "GET" as const,
    path: "/api/agents/tasks/:taskId/collections",
    PathVars: TaskCollectionsPathVarsSchema,
    Response: ApiSuccessSchema(CollectionsAttachmentResponseSchema)
  },
  AttachToTask: {
    method: "POST" as const,
    path: "/api/agents/tasks/:taskId/collections",
    PathVars: TaskCollectionsPathVarsSchema,
    Body: CollectionIdsBodySchema,
    Response: ApiSuccessSchema(AttachmentActionResponseSchema)
  },
  DetachFromTask: {
    method: "DELETE" as const,
    path: "/api/agents/tasks/:taskId/collections",
    PathVars: TaskCollectionsPathVarsSchema,
    Body: CollectionIdsBodySchema,
    Response: ApiSuccessSchema(AttachmentActionResponseSchema)
  },
  GetAgentCollections: {
    method: "GET" as const,
    path: "/api/agents/:agentId/collections",
    PathVars: AgentCollectionsPathVarsSchema,
    Response: ApiSuccessSchema(CollectionsAttachmentResponseSchema)
  },
  AttachToAgent: {
    method: "POST" as const,
    path: "/api/agents/:agentId/collections",
    PathVars: AgentCollectionsPathVarsSchema,
    Body: CollectionIdsBodySchema,
    Response: ApiSuccessSchema(AttachmentActionResponseSchema)
  },
  DetachFromAgent: {
    method: "DELETE" as const,
    path: "/api/agents/:agentId/collections",
    PathVars: AgentCollectionsPathVarsSchema,
    Body: CollectionIdsBodySchema,
    Response: ApiSuccessSchema(AttachmentActionResponseSchema)
  }
} as const;
